import { FormEvent, lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useSearchChurches, useRequestChurch } from '../hooks/useChurches'
import { geocodeAddress } from '../lib/geocode'
import { PublicLayout } from '../components/layout/PublicLayout'
import { Icons } from '../components/ui/Icons'
import { useIsMobile } from '../hooks/useIsMobile'
import { useCurrentPosition } from '../hooks/useCurrentPosition'
import type { Church } from '@fellowship/shared'
import {
  MapPin,
  Search,
  SlidersHorizontal,
  X,
  Map,
  List,
  Check,
  ArrowRight,
  ChevronDown,
} from 'lucide-react'
import { useAuthStore } from '../store/auth'

const ChurchMap = lazy(() => import('../components/ChurchMap'))

const DEFAULT_RADIUS = 25
const GRADIENT = 'linear-gradient(135deg, #3B4CCA 0%, #7C5CD3 100%)'

const DENOMINATION_OPTIONS = [
  { value: 'seventh-day-adventist', label: 'Seventh-day Adventist' },
  { value: 'baptist', label: 'Baptist' },
  { value: 'methodist', label: 'Methodist' },
  { value: 'catholic', label: 'Catholic' },
  { value: 'non-denominational', label: 'Non-denominational' },
  { value: 'lutheran', label: 'Lutheran' },
  { value: 'presbyterian', label: 'Presbyterian' },
]

function formatDistance(meters: number) {
  const miles = meters / 1609.34
  return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`
}

// ─── FilterCheckRow ───────────────────────────────────────────────────────────

function FilterCheckRow({
  label,
  checked,
  onToggle,
}: {
  label: string
  checked: boolean
  onToggle: () => void
}) {
  return (
    <div
      onClick={onToggle}
      style={{
        padding: '7px 8px',
        borderRadius: 8,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        userSelect: 'none',
        background: checked ? 'var(--primary-soft)' : 'transparent',
      }}
    >
      <div
        style={{
          width: 16,
          height: 16,
          borderRadius: 4,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: checked ? 'var(--primary)' : 'transparent',
          border: checked ? 'none' : '1.5px solid var(--line)',
        }}
      >
        {checked && <Check size={10} color="#fff" strokeWidth={3} />}
      </div>
      <span
        style={{
          fontSize: 13,
          color: checked ? 'var(--primary-deep)' : 'var(--ink)',
          fontWeight: checked ? 500 : 400,
        }}
      >
        {label}
      </span>
    </div>
  )
}

// ─── FiltersPopover ───────────────────────────────────────────────────────────

interface FiltersPopoverProps {
  anchorRef: React.RefObject<HTMLButtonElement | null>
  denomination: string
  claimedOnly: boolean
  hasUpcomingEvents: boolean
  resultCount: number
  onApply: (denomination: string, claimedOnly: boolean, hasUpcomingEvents: boolean) => void
  onClose: () => void
}

function FiltersPopover({
  anchorRef,
  denomination,
  claimedOnly,
  hasUpcomingEvents,
  resultCount,
  onApply,
  onClose,
}: FiltersPopoverProps) {
  const [pendingDenomination, setPendingDenomination] = useState(denomination)
  const [pendingClaimedOnly, setPendingClaimedOnly] = useState(claimedOnly)
  const [pendingHasUpcoming, setPendingHasUpcoming] = useState(hasUpcomingEvents)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      )
        onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [anchorRef, onClose])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const pendingCount =
    (pendingDenomination ? 1 : 0) + (pendingClaimedOnly ? 1 : 0) + (pendingHasUpcoming ? 1 : 0)

  return (
    <div
      ref={popoverRef}
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: 8,
        width: 320,
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 16,
        zIndex: 100,
        overflow: 'hidden',
        boxShadow: '0 24px 48px -16px rgba(31,26,46,.22), 0 4px 10px -4px rgba(31,26,46,.12)',
      }}
    >
      <div style={{ padding: '16px 16px 4px' }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--muted)',
            marginBottom: 8,
          }}
        >
          Denomination
        </div>
        <select
          value={pendingDenomination}
          onChange={(e) => setPendingDenomination(e.target.value)}
          aria-label="Denomination"
          className="input"
          style={{ marginBottom: 12, cursor: 'pointer' }}
        >
          <option value="">Any denomination</option>
          {DENOMINATION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--muted)',
            margin: '4px 0 6px',
          }}
        >
          Availability
        </div>
        <FilterCheckRow
          label="Claimed churches only"
          checked={pendingClaimedOnly}
          onToggle={() => setPendingClaimedOnly((v) => !v)}
        />
        <FilterCheckRow
          label="Has upcoming events"
          checked={pendingHasUpcoming}
          onToggle={() => setPendingHasUpcoming((v) => !v)}
        />
      </div>

      <div
        style={{
          padding: '12px 16px',
          borderTop: '1px solid var(--line-soft)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--surface-2)',
        }}
      >
        <button
          onClick={() => {
            setPendingDenomination('')
            setPendingClaimedOnly(false)
            setPendingHasUpcoming(false)
          }}
          style={{
            background: 'transparent',
            border: 0,
            fontSize: 12,
            color: 'var(--muted)',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Clear all
        </button>
        <button
          onClick={() => {
            onApply(pendingDenomination, pendingClaimedOnly, pendingHasUpcoming)
            onClose()
          }}
          style={{
            height: 34,
            padding: '0 16px',
            borderRadius: 999,
            background: GRADIENT,
            color: '#fff',
            border: 0,
            cursor: 'pointer',
            fontSize: 12.5,
            fontWeight: 600,
            fontFamily: 'inherit',
          }}
        >
          {pendingCount === 0
            ? 'Show results'
            : `Show ${resultCount} church${resultCount !== 1 ? 'es' : ''}`}
        </button>
      </div>
    </div>
  )
}

// ─── ChurchCard ───────────────────────────────────────────────────────────────

function ChurchCard({
  church,
  selected,
  onClick,
}: {
  church: Church
  selected?: boolean
  onClick?: () => void
}) {
  return (
    <article
      onClick={onClick}
      data-church-id={church.id}
      style={{
        display: 'flex',
        gap: 14,
        padding: '14px 16px',
        borderRadius: 16,
        cursor: 'pointer',
        background: selected ? 'var(--primary-soft)' : 'var(--surface)',
        border: `${selected ? 2 : 1}px solid ${selected ? 'var(--primary)' : 'var(--line-soft)'}`,
        margin: selected ? '-1px 0' : 0,
        transition: 'background 0.15s ease, border-color 0.15s ease',
      }}
    >
      {/* Logo / initial */}
      {church.logoUrl ? (
        <img
          src={church.logoUrl}
          alt={`${church.name} logo`}
          style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }}
        />
      ) : (
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 12,
            flexShrink: 0,
            background: GRADIENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 22,
            fontWeight: 700,
            fontFamily: 'Newsreader, serif',
          }}
        >
          {church.name.charAt(0)}
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 4,
            flexWrap: 'wrap',
          }}
        >
          {church.verified && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 18,
                padding: '0 7px',
                borderRadius: 999,
                background: '#DCFCE7',
                border: '1px solid #86EFAC',
                fontSize: 10.5,
                color: '#166534',
                fontWeight: 600,
              }}
            >
              Verified
            </span>
          )}
          {church.distanceMeters !== undefined && (
            <span
              style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--muted)', fontWeight: 500 }}
            >
              {formatDistance(church.distanceMeters)}
            </span>
          )}
        </div>

        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--ink)',
            lineHeight: 1.2,
            marginBottom: 3,
          }}
        >
          <Link
            to={`/churches/${church.slug}`}
            onClick={(e) => e.stopPropagation()}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            {church.name}
          </Link>
        </div>

        <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 6 }}>
          {[
            church.denomination?.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
            church.city,
            church.state,
          ]
            .filter(Boolean)
            .join(' · ')}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {church.followerCount > 0 && (
            <span
              style={{
                fontSize: 11,
                color: 'var(--muted)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Icons.users size={11} /> {church.followerCount}{' '}
              {church.followerCount === 1 ? 'follower' : 'followers'}
            </span>
          )}
          <Link
            to={`/churches/${church.slug}`}
            onClick={(e) => e.stopPropagation()}
            style={{ marginLeft: 'auto' }}
          >
            <button
              style={{
                height: 28,
                padding: '0 12px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontFamily: 'inherit',
                background: selected ? GRADIENT : 'transparent',
                color: selected ? '#fff' : 'var(--primary)',
                border: selected ? 'none' : '1.5px solid var(--primary)',
              }}
            >
              View <ArrowRight size={11} />
            </button>
          </Link>
        </div>
      </div>
    </article>
  )
}

// ─── RequestChurchCta ─────────────────────────────────────────────────────────

function RequestChurchCta() {
  const request = useRequestChurch()
  const [open, setOpen] = useState(false)
  const [churchName, setChurchName] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    request.mutate(
      { churchName: churchName.trim(), websiteUrl: websiteUrl.trim() || undefined },
      {
        onSuccess: () => {
          setOpen(false)
        },
      },
    )
  }

  if (request.isSuccess) {
    return (
      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <p style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 500, margin: 0 }}>
          Thanks! We'll add {churchName} to our list.
        </p>
        <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
          Are you the church admin?{' '}
          <Link
            to="/churches/new"
            style={{ color: 'var(--ink-2)', textDecoration: 'none', fontWeight: 500 }}
          >
            Create your church page →
          </Link>
        </p>
      </div>
    )
  }

  return (
    <div style={{ marginTop: 24, borderTop: '1px solid var(--line-soft)', paddingTop: 20 }}>
      {!open ? (
        <p style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', margin: 0 }}>
          Don't see your church?{' '}
          <button
            type="button"
            onClick={() => setOpen(true)}
            style={{
              background: 'transparent',
              border: 0,
              padding: 0,
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--primary)',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Request it →
          </button>
        </p>
      ) : (
        <div
          style={{
            maxWidth: 380,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <p style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)', margin: 0 }}>
            Request a church
          </p>
          <p style={{ fontSize: 12.5, color: 'var(--muted)', margin: 0 }}>
            We'll add it to our directory. You'll be able to follow it once it's listed.
          </p>
          <input
            className="input"
            type="text"
            placeholder="Church name"
            aria-label="Church name"
            value={churchName}
            onChange={(e) => setChurchName(e.target.value)}
            required
            autoFocus
          />
          <input
            className="input"
            type="url"
            placeholder="Website (optional)"
            aria-label="Church website"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
          />
          {request.isError && (
            <p style={{ fontSize: 12, color: 'var(--warn)', margin: 0 }}>
              {(request.error as Error)?.message ?? 'Something went wrong. Try again.'}
            </p>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="submit"
              disabled={request.isPending || !churchName.trim()}
              className="btn"
              onClick={handleSubmit}
            >
              {request.isPending ? 'Sending…' : 'Send request'}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{
                background: 'transparent',
                border: 0,
                fontSize: 13,
                color: 'var(--muted)',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Cancel
            </button>
          </div>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: 0 }}>
            Are you the pastor or admin?{' '}
            <Link
              to="/churches/new"
              style={{ color: 'var(--ink-2)', textDecoration: 'none', fontWeight: 500 }}
            >
              Create your church page directly →
            </Link>
          </p>
        </div>
      )}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ChurchesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuthStore()
  const isMobile = useIsMobile()

  const initLat = parseFloat(searchParams.get('lat') ?? '') || (user?.locationLat ?? 34.0522)
  const initLng = parseFloat(searchParams.get('lng') ?? '') || (user?.locationLng ?? -118.2437)
  const initRadius = parseInt(searchParams.get('radius') ?? '') || DEFAULT_RADIUS
  const initQ = searchParams.get('q') ?? ''
  const validDenominations = new Set(DENOMINATION_OPTIONS.map((o) => o.value))
  const initDenomination = searchParams.get('denomination') ??
    (user?.denomination && validDenominations.has(user.denomination) ? user.denomination : '')
  const initClaimedOnly = searchParams.get('claimedOnly') === 'true'
  const initHasUpcoming = searchParams.get('hasUpcomingEvents') === 'true'

  const [lat, setLat] = useState(initLat)
  const [lng, setLng] = useState(initLng)
  const [radius, setRadius] = useState(initRadius)
  const [locationText, setLocationText] = useState(initQ || user?.locationLabel || '')
  const [denomination, setDenomination] = useState(initDenomination)
  const [claimedOnly, setClaimedOnly] = useState(initClaimedOnly)
  const [hasUpcomingEvents, setHasUpcomingEvents] = useState(initHasUpcoming)
  const [selectedId, setSelectedId] = useState<string | undefined>()
  const [view, setView] = useState<'map' | 'list'>(() =>
    window.matchMedia?.('(max-width: 767px)').matches ? 'list' : 'map'
  )
  const [showFilters, setShowFilters] = useState(false)
  const [textFilter, setTextFilter] = useState('')
  const currentPosition = useCurrentPosition()
  const currentPositionApplied = useRef(false)
  const filtersButtonRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const { data, isLoading, isError } = useSearchChurches({
    lat,
    lng,
    radius,
    denomination: denomination || undefined,
    claimedOnly: claimedOnly || undefined,
    hasUpcomingEvents: hasUpcomingEvents || undefined,
  })

  const churches = data?.data ?? []
  const total = data?.meta?.total ?? 0

  const needle = textFilter.trim().toLowerCase()
  const visibleChurches = needle
    ? churches.filter((c) =>
        c.name.toLowerCase().includes(needle) ||
        (c.city ?? '').toLowerCase().includes(needle) ||
        c.denomination.toLowerCase().includes(needle),
      )
    : churches
  const center = { lat, lng }

  const activeFilterCount =
    (denomination ? 1 : 0) + (claimedOnly ? 1 : 0) + (hasUpcomingEvents ? 1 : 0)

  // Sync state to URL
  // Current position wins over profile location (highest priority after URL params)
  useEffect(() => {
    if (searchParams.get('lat') || searchParams.get('lng')) return
    if (!currentPosition) return
    currentPositionApplied.current = true
    setLat(currentPosition.lat)
    setLng(currentPosition.lng)
    if (currentPosition.label && !searchParams.get('q')) setLocationText(currentPosition.label)
  }, [currentPosition]) // eslint-disable-line react-hooks/exhaustive-deps

  // Profile location fallback — only if geolocation didn't fire
  useEffect(() => {
    if (searchParams.get('lat') || searchParams.get('lng')) return
    if (currentPositionApplied.current) return
    if (user?.locationLat && user?.locationLng) {
      setLat(user.locationLat)
      setLng(user.locationLng)
      if (user.locationLabel && !searchParams.get('q')) setLocationText(user.locationLabel)
    }
  }, [user?.locationLat, user?.locationLng]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        p.set('lat', String(lat))
        p.set('lng', String(lng))
        p.set('radius', String(radius))
        if (denomination) p.set('denomination', denomination)
        else p.delete('denomination')
        if (claimedOnly) p.set('claimedOnly', 'true')
        else p.delete('claimedOnly')
        if (hasUpcomingEvents) p.set('hasUpcomingEvents', 'true')
        else p.delete('hasUpcomingEvents')
        return p
      },
      { replace: true },
    )
  }, [lat, lng, radius, denomination, claimedOnly, hasUpcomingEvents]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleLocationSearch(e: React.FormEvent) {
    e.preventDefault()
    if (!locationText.trim()) return
    const result = await geocodeAddress(locationText.trim())
    if (result) {
      setLat(result.lat)
      setLng(result.lng)
    }
  }

  const handleApplyFilters = useCallback(
    (nextDenomination: string, nextClaimedOnly: boolean, nextHasUpcoming: boolean) => {
      setDenomination(nextDenomination)
      setClaimedOnly(nextClaimedOnly)
      setHasUpcomingEvents(nextHasUpcoming)
    },
    [],
  )

  const handleCloseFilters = useCallback(() => setShowFilters(false), [])

  useEffect(() => {
    if (!selectedId || !listRef.current) return
    const el = listRef.current.querySelector(`[data-church-id="${selectedId}"]`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [selectedId])

  return (
    <PublicLayout activeNav="Churches" hideFooter>
      {/* ── Search toolbar row ──────────────────────────────────────────── */}
      <div
        style={{
          padding: isMobile ? '8px 12px' : '10px 32px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          borderBottom: '1px solid var(--line-soft)',
          background: 'var(--bg)',
          flexShrink: 0,
        }}
      >
        {/* Location pill */}
        <form
          onSubmit={handleLocationSearch}
          style={{
            flex: isMobile ? 1 : undefined,
            width: isMobile ? undefined : 400,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            height: 44,
            background: 'var(--surface)',
            border: '1px solid var(--line-soft)',
            borderRadius: 999,
            padding: '0 6px 0 14px',
            gap: 8,
          }}
        >
          <MapPin size={14} color="var(--muted)" style={{ flexShrink: 0 }} />

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--muted)',
                lineHeight: 1,
              }}
            >
              Where
            </div>
            <input
              value={locationText}
              onChange={(e) => setLocationText(e.target.value)}
              placeholder="City, zip, or address…"
              aria-label="Search location"
              style={{
                flex: 1,
                border: 0,
                background: 'transparent',
                outline: 'none',
                fontSize: 13.5,
                fontWeight: 500,
                fontFamily: 'inherit',
                color: 'var(--ink)',
                minWidth: 0,
              }}
            />
          </div>
          <button
            type="submit"
            aria-label="Search"
            style={{
              height: 36,
              padding: '0 18px',
              borderRadius: 999,
              background: GRADIENT,
              color: '#fff',
              border: 0,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'inherit',
              flexShrink: 0,
              boxShadow: '0 4px 12px rgba(59,76,202,.3)',
            }}
          >
            <Search size={14} /> Search
          </button>
        </form>

        {/* Filters button */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <button
            ref={filtersButtonRef}
            onClick={() => setShowFilters((v) => !v)}
            style={{
              height: 42,
              padding: '0 14px',
              borderRadius: 999,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 500,
              border: `1.5px solid ${activeFilterCount ? 'var(--primary)' : 'var(--line)'}`,
              background: activeFilterCount ? 'var(--primary-soft)' : 'var(--surface)',
              color: activeFilterCount ? 'var(--primary-deep)' : 'var(--ink-2)',
            }}
          >
            <SlidersHorizontal size={13} />
            Filters
            {activeFilterCount > 0 && (
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: 'var(--primary)',
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {activeFilterCount}
              </span>
            )}
            <ChevronDown size={12} />
          </button>

          {showFilters && (
            <FiltersPopover
              anchorRef={filtersButtonRef}
              denomination={denomination}
              claimedOnly={claimedOnly}
              hasUpcomingEvents={hasUpcomingEvents}
              resultCount={total}
              onApply={handleApplyFilters}
              onClose={handleCloseFilters}
            />
          )}
        </div>

        {/* Map / List toggle */}
        <button
          onClick={() => setView(view === 'map' ? 'list' : 'map')}
          style={{
            height: 42,
            padding: '0 16px',
            borderRadius: 999,
            border: '1px solid var(--line)',
            background: 'var(--surface)',
            color: 'var(--ink-2)',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: 'inherit',
            flexShrink: 0,
          }}
        >
          {view === 'map' ? (
            <>
              <List size={14} /> List
            </>
          ) : (
            <>
              <Map size={14} /> Map
            </>
          )}
        </button>

        {!isMobile && <div style={{ flex: 1 }} />}
        {/* Radius slider — desktop only */}
        {!isMobile && <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
            Within {radius} mi
          </span>
          <input
            type="range"
            min={5}
            max={100}
            step={5}
            value={radius}
            aria-label="Radius"
            onChange={(e) => setRadius(parseInt(e.target.value))}
            style={{ width: 100, accentColor: 'var(--primary)' }}
          />
        </div>}
      </div>

      {/* ── Active filter chips row (only when filters are active) ───────── */}
      {activeFilterCount > 0 && (
        <div
          style={{
            padding: '8px 32px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            borderBottom: '1px solid var(--line-soft)',
            background: 'var(--bg)',
            flexShrink: 0,
            flexWrap: 'wrap',
          }}
        >
          {/* Active filter chips */}
          {denomination && (
            <span
              style={{
                height: 32,
                padding: '0 12px',
                borderRadius: 999,
                background: 'var(--primary-soft)',
                border: '1.5px solid var(--primary)',
                color: 'var(--primary-deep)',
                fontSize: 12.5,
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {DENOMINATION_OPTIONS.find((d) => d.value === denomination)?.label ?? denomination}
              <button
                onClick={() => setDenomination('')}
                aria-label="Remove denomination filter"
                style={{
                  background: 'transparent',
                  border: 0,
                  padding: 0,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  lineHeight: 1,
                }}
              >
                <X size={12} color="var(--primary)" />
              </button>
            </span>
          )}
          {claimedOnly && (
            <span
              style={{
                height: 32,
                padding: '0 12px',
                borderRadius: 999,
                background: 'var(--primary-soft)',
                border: '1.5px solid var(--primary)',
                color: 'var(--primary-deep)',
                fontSize: 12.5,
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              Claimed only
              <button
                onClick={() => setClaimedOnly(false)}
                aria-label="Remove claimed filter"
                style={{
                  background: 'transparent',
                  border: 0,
                  padding: 0,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  lineHeight: 1,
                }}
              >
                <X size={12} color="var(--primary)" />
              </button>
            </span>
          )}
          {hasUpcomingEvents && (
            <span
              style={{
                height: 32,
                padding: '0 12px',
                borderRadius: 999,
                background: 'var(--primary-soft)',
                border: '1.5px solid var(--primary)',
                color: 'var(--primary-deep)',
                fontSize: 12.5,
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              Has upcoming events
              <button
                onClick={() => setHasUpcomingEvents(false)}
                aria-label="Remove upcoming events filter"
                style={{
                  background: 'transparent',
                  border: 0,
                  padding: 0,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  lineHeight: 1,
                }}
              >
                <X size={12} color="var(--primary)" />
              </button>
            </span>
          )}
        </div>
      )}

      {/* ── Content area ────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        {/* List panel */}
        <div
          style={{
            width: (view === 'map' && !isMobile) ? 420 : '100%',
            flexShrink: 0,
            display: (isMobile && view === 'map') ? 'none' : 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRight: (!isMobile && view === 'map') ? '1px solid var(--line-soft)' : 'none',
          }}
        >
          {/* Result count + inline text filter */}
          <div
            style={{
              padding: '10px 16px',
              borderBottom: '1px solid var(--line-soft)',
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>
              {isLoading ? 'Searching…' : isError ? (
                <span style={{ color: 'var(--warn)' }}>Failed to load results</span>
              ) : (
                `${needle ? visibleChurches.length : total} ${(needle ? visibleChurches.length : total) === 1 ? 'church' : 'churches'} found`
              )}
            </span>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                background: 'var(--surface)',
                border: '1.5px solid var(--line-soft)',
                borderRadius: 999,
              }}
            >
              <Search size={13} color="var(--muted)" style={{ flexShrink: 0 }} />
              <input
                value={textFilter}
                onChange={(e) => setTextFilter(e.target.value)}
                placeholder="Filter by name, city, or denomination…"
                aria-label="Filter results"
                style={{
                  border: 0, background: 'transparent', outline: 'none',
                  fontSize: 13, fontFamily: 'inherit', color: 'var(--ink)', flex: 1,
                }}
              />
              {textFilter && (
                <button
                  onClick={() => setTextFilter('')}
                  aria-label="Clear filter"
                  style={{ background: 'transparent', border: 0, cursor: 'pointer', padding: 0, color: 'var(--muted)', display: 'flex' }}
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          {/* Cards */}
          <div
            ref={listRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '12px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {!isLoading && !isError && churches.length === 0 && (
              <div style={{ paddingTop: 48, textAlign: 'center' }}>
                <p style={{ fontSize: 14, color: 'var(--muted)', margin: '0 0 8px' }}>
                  No churches found.
                </p>
                <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0 }}>
                  Try a different location or adjust your filters.
                </p>
              </div>
            )}

            {!isLoading && !isError && needle && visibleChurches.length === 0 && churches.length > 0 && (
              <div style={{ paddingTop: 48, textAlign: 'center' }}>
                <p style={{ fontSize: 14, color: 'var(--muted)', margin: 0 }}>
                  No churches match "{textFilter}".
                </p>
              </div>
            )}

            {visibleChurches.map((church) => (
              <ChurchCard
                key={church.id}
                church={church}
                selected={selectedId === church.id}
                onClick={() => setSelectedId(church.id === selectedId ? undefined : church.id)}
              />
            ))}

            {!isLoading && !isError && <RequestChurchCta />}
          </div>
        </div>

        {/* Map panel */}
        {view === 'map' && (
          <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
            <Suspense fallback={<div style={{ height: '100%', background: 'var(--surface-2)' }} />}>
              <ChurchMap
                churches={churches}
                center={center}
                onSelect={(church) =>
                  setSelectedId(church.id === selectedId ? undefined : church.id)
                }
                selectedId={selectedId}
              />
            </Suspense>
          </div>
        )}
      </div>
    </PublicLayout>
  )
}
